import React, { useState, useMemo, useCallback } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_BLUE = "#1a73e8";

function getInitials(addr = "") {
  const name = addr.replace(/<[^>]+>/g, "").replace(/"/g, "").trim() || addr;
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function getDisplayName(addr = "") {
  const m = addr.match(/^"?([^"<]+)"?\s*</);
  if (m) return m[1].trim();
  const atIdx = addr.indexOf("@");
  return atIdx > -1 ? addr.slice(0, atIdx) : addr;
}

function parseAddrs(str) {
  if (!str) return [];
  return String(str).split(",").map((a) => a.trim()).filter(Boolean);
}

function isHtml(str = "") {
  return /<[a-z][\s\S]*>/i.test(str.trim());
}

function isMarkdown(str = "") {
  if (!str || isHtml(str)) return false;
  return /(^|\n)(#{1,3}\s|\*\*|[*+-]\s|\d+\.\s)/m.test(str);
}

/** Empty body sentinel from `format_email_thread` (`*No body content*`). */
function isNoBodyPlaceholder(str = "") {
  return /^\*No body content\*$/i.test(String(str).trim());
}

function toSnippet(body = "") {
  const clean = body
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  return clean.length > 100 ? clean.slice(0, 100) + "…" : clean;
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ addr, size = 40, fontSize = 14 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: AVATAR_BLUE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 500,
        fontSize,
        flexShrink: 0,
        userSelect: "none",
        letterSpacing: "0.3px",
      }}
    >
      {getInitials(addr)}
    </Box>
  );
}

// ─── MetaAddresses ────────────────────────────────────────────────────────────

function MetaAddresses({ label, addrs }) {
  if (!addrs || addrs.length === 0) return null;
  return (
    <Box sx={{ display: "flex", gap: "3px", alignItems: "baseline", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>{label}</Typography>
      {addrs.map((a, i) => (
        <React.Fragment key={a}>
          <Typography component="span" sx={{ fontSize: "12px", color: "text.primary" }}>
            {getDisplayName(a)}
          </Typography>
          {i < addrs.length - 1 && (
            <Typography component="span" sx={{ fontSize: "12px", color: "text.secondary" }}>,</Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

function formatHeaderLabel(key) {
  if (!key) return "";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// ─── DetailBox ────────────────────────────────────────────────────────────────

function DetailBox({ email }) {
  const toAddrs = parseAddrs(email.to);
  const ccAddrs = parseAddrs(email.cc);
  const bccAddrs = parseAddrs(email.bcc);

  const rows = [
    { label: "from", value: email.from },
    toAddrs.length && { label: "to", value: toAddrs.join(", ") },
    ccAddrs.length && { label: "cc", value: ccAddrs.join(", ") },
    bccAddrs.length && { label: "bcc", value: bccAddrs.join(", ") },
    email.date && { label: "date", value: email.date },
    email.subject && { label: "subject", value: email.subject },
  ].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: "action.hover",
        borderRadius: "8px",
        px: 2,
        py: 1.25,
        mb: 2,
        lineHeight: 1.9,
      }}
    >
      {rows.map(({ label, value }) => (
        <Box key={label} sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
          <Typography
            component="span"
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              minWidth: "4.25rem",
              textAlign: "left",
              flexShrink: 0,
            }}
          >
            {formatHeaderLabel(label)}:
          </Typography>
          <Typography component="span" sx={{ fontSize: "12px", color: "text.primary", wordBreak: "break-word" }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── EmailBody ────────────────────────────────────────────────────────────────

function EmailBody({ body, theme }) {
  const mdComponents = useMemo(
    () => ({
      p: ({ children }) => (
        <Typography component="p" sx={{ m: "0.3rem 0", fontSize: "inherit", lineHeight: "inherit" }}>
          {children}
        </Typography>
      ),
      strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 600 }}>{children}</Box>,
      ul: ({ children }) => <Box component="ul" sx={{ m: "0.3rem 0", pl: "1.25rem" }}>{children}</Box>,
      ol: ({ children }) => <Box component="ol" sx={{ m: "0.3rem 0", pl: "1.25rem" }}>{children}</Box>,
      li: ({ children }) => <Box component="li" sx={{ m: "0.1rem 0" }}>{children}</Box>,
      h1: ({ children }) => (
        <Typography component="h3" sx={{ fontSize: "0.92rem", fontWeight: 600, mt: 1, mb: 0.35 }}>{children}</Typography>
      ),
      h2: ({ children }) => (
        <Typography component="h3" sx={{ fontSize: "0.88rem", fontWeight: 600, mt: 0.85, mb: 0.3 }}>{children}</Typography>
      ),
      h3: ({ children }) => (
        <Typography component="h4" sx={{ fontSize: "0.85rem", fontWeight: 600, mt: 0.75, mb: 0.25 }}>{children}</Typography>
      ),
      a: ({ href, children }) => (
        <Box
          component="a"
          href={href}
          sx={{ color: theme?.palette?.primary?.main ?? AVATAR_BLUE, textDecoration: "underline" }}
        >
          {children}
        </Box>
      ),
    }),
    [theme]
  );

  const bodyRaw = (body || "").trim();
  if (isNoBodyPlaceholder(bodyRaw)) {
    return (
      <Typography
        sx={{
          fontSize: "13.5px",
          lineHeight: 1.75,
          fontStyle: "italic",
          color: "text.secondary",
          opacity: 0.72,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        No body content
      </Typography>
    );
  }

  const bodyHtml = isHtml(bodyRaw);
  const bodyMd = !bodyHtml && isMarkdown(bodyRaw);

  const baseSx = {
    fontSize: "13.5px",
    lineHeight: 1.75,
    color: "text.primary",
    "& p:first-of-type": { mt: 0 },
    "& p:last-child": { mb: 0 },
  };

  if (bodyHtml) {
    return (
      <Box
        sx={{ ...baseSx, "& a": { color: theme?.palette?.primary?.main ?? AVATAR_BLUE } }}
        dangerouslySetInnerHTML={{ __html: bodyRaw }}
      />
    );
  }

  if (bodyMd) {
    return (
      <Box sx={baseSx}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {bodyRaw}
        </ReactMarkdown>
      </Box>
    );
  }

  return (
    <Typography sx={{ ...baseSx, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {bodyRaw || "—"}
    </Typography>
  );
}

// ─── CollapsedRow ─────────────────────────────────────────────────────────────

function CollapsedRow({ email, idx, onToggle }) {
  const bodyTrim = (email.body || "").trim();
  const emptyBody = isNoBodyPlaceholder(bodyTrim);
  const raw = emptyBody
    ? "No body content"
    : isMarkdown(email.body)
      ? toSnippet(email.body)
      : bodyTrim.replace(/\n/g, " ").trim().slice(0, 100);
  const snippet = raw.length >= 100 ? raw + "…" : raw;

  const shortDate = email.date
    ? email.date.split(",").slice(-1)[0]?.trim() ?? email.date
    : "";

  return (
    <Box
      onClick={() => onToggle(idx)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: "20px",
        py: "9px",
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        transition: "background 0.1s",
        "&:hover": { bgcolor: "action.hover" },
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Avatar addr={email.from} size={32} fontSize={12} />

      <Typography
        sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {getDisplayName(email.from)}
      </Typography>

      <Typography
        sx={{
          fontSize: "12.5px",
          color: "text.secondary",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          ...(emptyBody && { fontStyle: "italic", opacity: 0.72 }),
        }}
      >
        {snippet}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
        {shortDate && (
          <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
            {shortDate}
          </Typography>
        )}
        <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      </Box>
    </Box>
  );
}

// ─── ExpandedRow ──────────────────────────────────────────────────────────────

function ExpandedRow({ email, idx, isLast, onToggle, theme }) {
  const [showDetail, setShowDetail] = useState(false);
  const toAddrs = parseAddrs(email.to);
  const ccAddrs = parseAddrs(email.cc);

  return (
    <Box>
      {/* Clickable header */}
      <Box
        onClick={() => onToggle(idx)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          px: "20px",
          pt: "16px",
          pb: "4px",
          cursor: "pointer",
          userSelect: "none",
          outline: "none",
          transition: "background 0.1s",
          "&:hover": { bgcolor: "action.hover" },
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Avatar addr={email.from} size={40} fontSize={14} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Sender + date + collapse icon */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: "text.primary" }}>
              {getDisplayName(email.from)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
              <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                {email.date}
              </Typography>
              <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            </Box>
          </Box>

          {/* Subject */}
          {email.subject && (
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary", mt: "2px" }}>
              {email.subject}
            </Typography>
          )}

          {/* To / CC */}
          <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mt: "4px", alignItems: "baseline" }}>
            {toAddrs.length > 0 && <MetaAddresses label="to" addrs={toAddrs} />}
            {ccAddrs.length > 0 && (
              <>
                <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>·</Typography>
                <MetaAddresses label="cc" addrs={ccAddrs} />
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ pl: "72px", pr: "20px", pb: "20px", pt: "12px" }}>
        {/* Show/hide details */}
        <Box
          onClick={(e) => { e.stopPropagation(); setShowDetail((v) => !v); }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            color: AVATAR_BLUE,
            cursor: "pointer",
            mb: "12px",
            fontSize: "12px",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {showDetail
            ? <ExpandLessIcon sx={{ fontSize: 16 }} />
            : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          {showDetail ? "Hide details" : "Show details"}
        </Box>

        <Collapse in={showDetail} timeout="auto" unmountOnExit>
          <DetailBox email={email} />
        </Collapse>

        <EmailBody body={email.body} theme={theme} />
      </Box>
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {Array<{
 *   from?: string,
 *   to?: string,
 *   cc?: string,
 *   bcc?: string,
 *   subject?: string,
 *   body?: string,
 *   date?: string,
 * }>} props.emailList
 * @param {import('@mui/material').Theme} [props.theme]
 */
const EmailThreadRenderer = ({ emailList = [], theme }) => {
  const lastIdx = emailList.length - 1;

  const [expandedSet, setExpandedSet] = useState(
    () => new Set(lastIdx >= 0 ? [lastIdx] : [])
  );

  const toggle = useCallback((idx) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2, overflow: "hidden" }}>
      {emailList.map((email, idx) => {
        const isLast = idx === emailList.length - 1;
        const expanded = expandedSet.has(idx);

        return (
          <Box
            key={idx}
            sx={{ borderBottom: isLast ? "none" : "1px solid", borderColor: "divider" }}
          >
            {/* Collapsed row — always rendered, hidden when expanded */}
            <Collapse in={!expanded} timeout={250} unmountOnExit>
              <CollapsedRow email={email} idx={idx} onToggle={toggle} />
            </Collapse>

            {/* Expanded row — slides in/out smoothly */}
            <Collapse in={expanded} timeout={300} unmountOnExit>
              <ExpandedRow
                email={email}
                idx={idx}
                isLast={isLast}
                onToggle={toggle}
                theme={theme}
              />
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default EmailThreadRenderer;